/**
 * 通用token管理
 */
import Cookie from 'js-cookie';
import request from '@/core/http-request.js';
import EventEmitter from './EventEmitter';
import { TokenProps } from './types/token';
import browser from './browser';
import { getRootDomain } from './utils';

// cookie的key
// token账号
const TOKEN = 'ovo_ticket'; // web的key
const TOKEN_H5 = 'nToken'; // 待h5项目全面接入后去掉
const TOKEN_INVALID = 'ovo_invalid_token'; // 超过可刷新时间的token
// token过期时间
const TOKEN_EXP = 'ovo_tokenValidity'; // web的key
const TOKEN_EXP_H5 = 'tokenEnd'; // 待h5项目全面接入后去掉
// token可刷新的有效期
const REFRESH_EXP = 'ovo_refreshTokenValidity'; // web的key
const REFRESH_EXP_H5 = 'refreshEnd'; // 待h5项目全面接入后去掉
// token刷新标识，1 刷新中，0 未刷新
const REFRESH_TIMER = 'ovo_refreshTimer';
// 第三方免登授权标识
const THIRD_LOGIN_STATUS = 'ovo_thirdLoginStatus';

// 共享cookie的根域名
const DOMAIN_ROOT = getRootDomain();

/** 获取数值 */
const getNumber = (str?: string) => {
  const num = Number(str);
  return isNaN(num) ? 0 : num;
};

/**
 * 获取cookie中的token
 * @returns token信息
 */
export const getCookieTokenInfo = (): TokenProps => {
  const tokenExp = getNumber(Cookie.get(TOKEN_EXP));
  const tokenExpH5 = getNumber(Cookie.get(TOKEN_EXP_H5));
  if (tokenExp && tokenExp > tokenExpH5) {
    // web端token有效期更久，按web端的key取token信息
    return {
      token: Cookie.get(TOKEN) || '',
      tokenExpirationTimestamp: tokenExp,
      refreshExpirationTimestamp: getNumber(Cookie.get(REFRESH_EXP)),
    };
  } else {
    return {
      token: Cookie.get(TOKEN_H5) || '',
      tokenExpirationTimestamp: tokenExpH5,
      refreshExpirationTimestamp: getNumber(Cookie.get(REFRESH_EXP_H5)),
    };
  }
};

export default class TokenMgmt extends EventEmitter {
  /** 是否在万店掌app内 */
  private isApp: boolean;
  /** 检查token刷新状态的次数 */
  private checkRefreshCount = 0;
  /** token刷新失败重试次数 */
  private refreshRetryCount = 0;
  /** 定时刷新token的timeout序号 */
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  /** iframe定时重新获取token的timeout序号 */
  private iframeTimeout: ReturnType<typeof setTimeout> | null = null;

  /** 万店掌token */
  token = '';
  /** token过期时间戳 */
  tokenExpirationTimestamp = 0;
  /** token刷新过期时间戳 */
  refreshExpirationTimestamp = 0;

  /**
   * 构造函数
   * @param isApp 是否在万店掌app内
   */
  constructor(isApp = false) {
    super();
    this.isApp = isApp;
    this.getCookieToken();

    // 初始化的时候移除cookie刷新中标识
    Cookie.remove(REFRESH_TIMER, { domain: DOMAIN_ROOT });

    // 页面进入后台以后，可能导致定时刷新不准确
    document.addEventListener('visibilitychange', this.onPageVisibilityChange.bind(this));
  }

  /**
   * 页面可见性改变
   */
  private onPageVisibilityChange() {
    if (!document.hidden) {
      // 页面显示，检查token有效期
      this.validateToken();
    }
  }

  /**
   * 移除token相关cookie，一级域名和全域名的cookie都移除
   */
  public removeCookies() {
    this.token = '';
    this.tokenExpirationTimestamp = 0;
    this.refreshExpirationTimestamp = 0;
    this.emit('token-refreshed', '');

    const cookieOptions = { domain: DOMAIN_ROOT };
    [
      TOKEN,
      TOKEN_H5,
      TOKEN_EXP,
      TOKEN_EXP_H5,
      REFRESH_EXP,
      REFRESH_EXP_H5,
      THIRD_LOGIN_STATUS,
    ].forEach((key) => {
      Cookie.remove(key, cookieOptions);
      Cookie.remove(key);
    });

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  /**
   * 获取cookie中的token
   * @returns token
   */
  public getCookieToken() {
    const tokenInfo = getCookieTokenInfo();
    // 写入属性中
    this.token = tokenInfo.token;
    this.tokenExpirationTimestamp = tokenInfo.tokenExpirationTimestamp;
    this.refreshExpirationTimestamp = tokenInfo.refreshExpirationTimestamp;
    return this.token;
  }

  /**
   * 设置token信息
   * @param props token信息，props存在，则需要更新cookie的值
   */
  setData(props?: TokenProps) {
    if (props) {
      // 写入新token时，先移除旧的cookie信息，避免token更新失败
      this.removeCookies();

      this.token = props.token;
      this.tokenExpirationTimestamp = props.tokenExpirationTimestamp;
      this.refreshExpirationTimestamp = props.refreshExpirationTimestamp;
      this.timedRefreshToken();

      // 更新cookie信息: token, token有效期, token刷新过期时间
      const expDate = new Date(this.refreshExpirationTimestamp);
      Cookie.set(TOKEN_H5, this.token, {
        domain: DOMAIN_ROOT,
        expires: expDate,
      });
      Cookie.set(TOKEN, this.token, {
        domain: DOMAIN_ROOT,
        expires: expDate,
      });
      Cookie.set(TOKEN_EXP_H5, this.tokenExpirationTimestamp.toString(), {
        domain: DOMAIN_ROOT,
        expires: new Date(this.tokenExpirationTimestamp),
      });
      Cookie.set(TOKEN_EXP, this.tokenExpirationTimestamp.toString(), {
        domain: DOMAIN_ROOT,
        expires: new Date(this.tokenExpirationTimestamp),
      });
      Cookie.set(REFRESH_EXP_H5, this.refreshExpirationTimestamp.toString(), {
        domain: DOMAIN_ROOT,
        expires: expDate,
      });
      Cookie.set(REFRESH_EXP, this.refreshExpirationTimestamp.toString(), {
        domain: DOMAIN_ROOT,
        expires: expDate,
      });
      Cookie.remove(THIRD_LOGIN_STATUS, { domain: DOMAIN_ROOT });
    } else {
      this.getCookieToken();
      return this.validateToken();
    }
  }

  /**
   * 校验token信息是否合法
   * @returns 0: 正常, 1: token即将过期，已调用刷新, -1: token已过期
   */
  private validateToken() {
    if (this.isApp) {
      // App内 不定时校验token了
      return 0;
    }
    if (browser.isIframe) {
      // iframe页面 定时重新获取token
      this.timedIframeToken();
      return 0;
    }
    if (!this.token) {
      // token不存在
      this.emit('invalid');
      return -1;
    }
    if (this.tokenExpirationTimestamp === 0 || this.refreshExpirationTimestamp === 0) {
      // 某些老项目写的cookie有问题，获取不到过期时间，则不校验token是否有效
      return 0;
    }

    const dateNow = Date.now();
    if (this.refreshExpirationTimestamp < dateNow) {
      // 已超过可刷新时间，清除token，把当前token备份到 ovo_invalid_token 中，有效期1个小时
      Cookie.set(TOKEN_INVALID, this.token, {
        domain: DOMAIN_ROOT,
        expires: dateNow + 1000 * 60 * 60,
      });
      this.token = '';
      const cookieOptions = { domain: DOMAIN_ROOT };
      [TOKEN, TOKEN_H5].forEach((key) => {
        Cookie.remove(key, cookieOptions);
        Cookie.remove(key);
      });
      this.emit('invalid');
      return -1;
    } else if (this.tokenExpirationTimestamp - dateNow < 1000 * 60 * 4.8) {
      // token有效期小于4.8分钟，立即刷新
      this.refreshToken();
      return 1;
    } else {
      // 定时刷新token
      this.timedRefreshToken();
      return 0;
    }
  }

  /**
   * 定时刷新token
   */
  private timedRefreshToken() {
    if (this.isApp) {
      // App内 不定时校验token了
      return;
    }
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
    if (this.tokenExpirationTimestamp === 0) {
      // 没有token过期时间，不定时刷新
      return;
    }
    if (browser.isIframe) {
      // iframe 页面定时重新获取token
      this.timedIframeToken();
      return 0;
    }

    const dateNow = Date.now();
    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      if (this.refreshExpirationTimestamp < dateNow) {
        // 已超过可刷新时间，需要重新登录
        this.emit('invalid');
      } else {
        // 刷新token
        console.log('--- 定时刷新 token ---');
        this.refreshToken();
      }
    }, this.tokenExpirationTimestamp - dateNow - 1000 * 60 * 4.8); // token到期前4.8分钟刷新token
  }

  /**
   * iframe页面定时获取token
   */
  private timedIframeToken() {
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
      this.iframeTimeout = null;
    }

    // iframe内 token到期前4.5分钟重新获取token
    this.tokenExpirationTimestamp &&
      (this.iframeTimeout = setTimeout(() => {
        this.iframeTimeout = null;
        const orgToken = this.token;
        this.setData();
        if (this.token && this.token !== orgToken) {
          // token 已改变，触发刷新通知
          this.emit('refreshed', this.token);
        }
      }, this.tokenExpirationTimestamp - Date.now() - 1000 * 60 * 4.5));
  }

  /**
   * 刷新token
   */
  private refreshToken() {
    if (this.isApp) {
      // App内 不定时校验token了
      // this.emit('refreshApp'); // 通知app刷新token
      return;
    }
    if (Cookie.get(REFRESH_TIMER) === '1') {
      // token正在被其他页面刷新
      this.checkRefreshCount = 0;
      this.checkRefreshStatus();
    } else {
      const tokenExp = getNumber(Cookie.get(TOKEN_EXP));
      const tokenExpH5 = getNumber(Cookie.get(TOKEN_EXP));
      if (Math.max(tokenExp, tokenExpH5) > this.tokenExpirationTimestamp) {
        // token过期时间已延长，说明token已经被刷新过
        this.setData();
        this.emit('refreshed', this.token);
        return;
      }

      // 设置cookie刷新中标识
      Cookie.set(REFRESH_TIMER, '1', {
        domain: DOMAIN_ROOT,
        expires: new Date(Date.now() + 1000 * 60 * 5),
      });

      // 调用api刷新token
      request({
        method: 'GET',
        url: '/ovopark-privilege/user/reFreshToken?token=' + this.token,
      })
        .then((data) => {
          this.refreshRetryCount = 0;
          this.setData(data as TokenProps);
          this.token && this.emit('refreshed', this.token);
        })
        .catch(() => {
          this.emit('refreshError', this.refreshRetryCount + 1);
          // 刷新失败，1分钟重试一次，不超过3次
          if (this.refreshRetryCount < 3) {
            this.refreshRetryCount++;
            // 不能直接把 this.refreshToken 作为 setTimeout 的回调函数，否则 this 指向 window
            setTimeout(() => this.refreshToken(), 1000 * 60);
          } else {
            this.refreshRetryCount = 0;
          }
        })
        .finally(() => {
          // 移除cookie刷新中标识
          Cookie.remove(REFRESH_TIMER, { domain: DOMAIN_ROOT });
        });
    }
  }

  /**
   * 检查token刷新状态
   */
  private checkRefreshStatus() {
    // token正在被其他页面刷新, 1s 检查一次是否刷新完成，最多持续60次
    if (this.checkRefreshCount >= 60) {
      return;
    }
    this.checkRefreshCount++;
    if (Cookie.get(REFRESH_TIMER) === '1') {
      // 不能直接把 this.checkRefreshStatus 作为 setTimeout 的回调函数，否则 this 指向 window
      setTimeout(() => this.checkRefreshStatus(), 1000);
    } else {
      // token已刷新，设置数据，发出刷新完成通知
      this.setData();
      this.token && this.emit('refreshed', this.token);
    }
  }
}
